import { z } from 'zod';

/**
 * The API error contract (PLAN.md §7).
 *
 * EVERY failure response has this shape, without exception. A consistent
 * envelope is what lets the client handle errors generically instead of
 * pattern-matching on strings, and what makes `requestId` reliably present when
 * a user reports a problem.
 */

/**
 * Machine-readable error codes.
 *
 * Codes are part of the public contract: clients branch on them, so renaming one
 * is a breaking change. Messages are for humans and may change freely.
 */
export const ERROR_CODES = [
  /* --- Authentication (§8.1) ------------------------------------------- */
  'UNAUTHENTICATED',
  'INVALID_CREDENTIALS',
  'TOKEN_EXPIRED',
  'TOKEN_REUSED', // refresh-token reuse detected; family revoked
  'EMAIL_NOT_VERIFIED',
  'MFA_REQUIRED',
  'STEP_UP_REQUIRED', // re-authenticate for a sensitive operation

  /* --- Authorization (§8.2) -------------------------------------------- */
  'FORBIDDEN',
  'NOT_A_MEMBER',
  // Distinct from NOT_A_MEMBER on purpose, for the identical reason
  // ORG_SUSPENDED is distinct from it one line down: "your OWN membership was
  // suspended" is not "you were never in this org", and collapsing the two
  // told a legitimately-still-a-member person the same confusing, unactionable
  // thing an outsider guessing a random org id would see — a real report,
  // not a hypothetical one (a member whose row this codebase's own platform
  // console showed as `status: suspended` got a bare "you are not a member",
  // silently lost their org selection, and landed back on the picker with no
  // idea why). Safe to split out for the same reason ORG_SUSPENDED already
  // is: `resolveOrgMembership` only ever asks about the CALLER'S OWN
  // membership row, so telling them its status leaks nothing about anyone
  // else's — the enumeration concern this file's own NOT_A_MEMBER comment
  // warns about only applies to "does this org exist", which stays collapsed
  // into NOT_A_MEMBER exactly as before (no row at all, for any reason).
  'MEMBERSHIP_SUSPENDED',
  // Distinct from NOT_A_MEMBER on purpose: "the org itself is suspended" is
  // not "you were never in this org", and a member who is still legitimately
  // a member deserves to be told what happened and what to do next (Phase 12
  // Wave 1, ai/phase-12-admin.md §3.3).
  'ORG_SUSPENDED',
  // Distinct from ORG_SUSPENDED too: a lapsed trial/subscription and an
  // operator's manual suspension are different columns, different writers,
  // and different next steps for an Owner (Phase 12 Wave 3,
  // ai/phase-12-wave3.md §3.2).
  'ORG_BILLING_LAPSED',
  // The org's PLAN does not include this module (Phase 12 Wave 4,
  // ai/phase-12-wave4-plans.md §3.4).
  //
  // A fourth 403 rather than reusing FORBIDDEN, and the distinction is the
  // whole point: FORBIDDEN means "your ROLE does not allow this", which no
  // amount of money changes, and PLAN_REQUIRED means "your role allows it and
  // your plan does not", which upgrading fixes. Collapsing them would tell an
  // Owner they lack permission on their own organization.
  //
  // It is NOT an authorization code. `can()` runs first and independently; a
  // caller who fails it gets FORBIDDEN whatever their plan says, and this is
  // only ever reached by someone whose role already permits the action. A
  // plan can therefore only ever REMOVE access, never grant it — which is what
  // keeps guardrail 7 ("never put a security control behind a flag") true
  // while plans ride the flag mechanism.
  'PLAN_REQUIRED',

  /* --- Resource --------------------------------------------------------- */
  // NOT_FOUND is deliberately returned for resources that exist but are not
  // visible to the caller. Distinguishing "does not exist" from "exists but you
  // may not see it" leaks the existence of other tenants' data.
  'NOT_FOUND',
  'ALREADY_EXISTS',
  'CONFLICT', // optimistic concurrency: `version` mismatch
  'GONE', // hard-deleted

  /* --- Input ------------------------------------------------------------ */
  'VALIDATION_FAILED',
  'UNSUPPORTED_MEDIA_TYPE',
  'PAYLOAD_TOO_LARGE',

  /* --- Limits ----------------------------------------------------------- */
  'RATE_LIMITED',
  'QUOTA_EXCEEDED', // includes telephony spend caps (§8.5)

  /* --- Server ----------------------------------------------------------- */
  'INTERNAL_ERROR',
  'SERVICE_UNAVAILABLE',
  'NOT_IMPLEMENTED',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    /**
     * Human-readable, safe to display.
     *
     * Must never contain internal detail — table names, SQL, stack traces, other
     * users' data, or whether a record exists in another tenant (§8.7).
     */
    message: z.string(),
    /** Field-level detail for VALIDATION_FAILED. Never contains secrets. */
    details: z.record(z.string(), z.unknown()).optional(),
    /** Correlates with server logs and the audit trail (§14). */
    requestId: z.string(),
    /** Present on RATE_LIMITED and QUOTA_EXCEEDED. */
    retryAfterSeconds: z.number().int().positive().optional(),
  }),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Default HTTP status per code. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  INVALID_CREDENTIALS: 401,
  TOKEN_EXPIRED: 401,
  TOKEN_REUSED: 401,
  EMAIL_NOT_VERIFIED: 403,
  MFA_REQUIRED: 401,
  STEP_UP_REQUIRED: 401,

  FORBIDDEN: 403,
  NOT_A_MEMBER: 403,
  MEMBERSHIP_SUSPENDED: 403,
  ORG_SUSPENDED: 403,
  ORG_BILLING_LAPSED: 403,
  PLAN_REQUIRED: 403,

  NOT_FOUND: 404,
  ALREADY_EXISTS: 409,
  CONFLICT: 409,
  GONE: 410,

  VALIDATION_FAILED: 400,
  UNSUPPORTED_MEDIA_TYPE: 415,
  PAYLOAD_TOO_LARGE: 413,

  RATE_LIMITED: 429,
  QUOTA_EXCEEDED: 429,

  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  NOT_IMPLEMENTED: 501,
};

export interface AppErrorOptions {
  readonly details?: Record<string, unknown>;
  readonly retryAfterSeconds?: number;
  readonly cause?: unknown;
}

/**
 * The only error type the API layer serializes to a client.
 *
 * Anything else that escapes a handler becomes INTERNAL_ERROR with a generic
 * message, so an unexpected exception cannot leak internals through its message.
 */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly retryAfterSeconds?: number;

  constructor(code: ErrorCode, message: string, options: AppErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    this.status = ERROR_STATUS[code];
    if (options.details !== undefined) this.details = options.details;
    if (options.retryAfterSeconds !== undefined) {
      this.retryAfterSeconds = options.retryAfterSeconds;
    }
  }

  /** Serializes to the wire contract. */
  toResponse(requestId: string): ApiError {
    return {
      error: {
        code: this.code,
        message: this.message,
        requestId,
        ...(this.details !== undefined && { details: this.details }),
        ...(this.retryAfterSeconds !== undefined && {
          retryAfterSeconds: this.retryAfterSeconds,
        }),
      },
    };
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/* -------------------------------------------------------------------------- *
 * Constructors for the common cases
 * -------------------------------------------------------------------------- */

export const errors = {
  unauthenticated: (message = 'Authentication required.') =>
    new AppError('UNAUTHENTICATED', message),

  /**
   * The single answer for every credential failure at login.
   *
   * Wrong password, unknown address, locked account and suspended account all
   * return this. Distinguishing them tells an attacker which addresses are
   * registered and whether their guessing is making progress — and for a B2B
   * product the account list is the customer list.
   */
  invalidCredentials: (message = 'Incorrect email or password.') =>
    new AppError('INVALID_CREDENTIALS', message),

  /**
   * Deliberately distinct from `invalidCredentials`: the caller has already
   * proven they know the password, so there is nothing left to disclose, and
   * "check your email" is the only useful thing to say.
   */
  emailNotVerified: (message = 'Please verify your email address before signing in.') =>
    new AppError('EMAIL_NOT_VERIFIED', message),

  tokenExpired: (message = 'Your session has expired. Please sign in again.') =>
    new AppError('TOKEN_EXPIRED', message),

  /**
   * A refresh token was presented twice (§8.1).
   *
   * By the time this is thrown the entire session has been revoked. The message
   * is deliberately plain — the interesting half of this event goes to the audit
   * log and an alert, not to whoever is holding the token.
   */
  tokenReused: (message = 'Your session has ended. Please sign in again.') =>
    new AppError('TOKEN_REUSED', message),

  stepUpRequired: (message = 'Please re-authenticate to continue.') =>
    new AppError('STEP_UP_REQUIRED', message),

  forbidden: (message = 'You do not have permission to perform this action.') =>
    new AppError('FORBIDDEN', message),

  /**
   * The caller's OWN membership row in this org exists but is not
   * `'active'` — distinct from `notAMember` for the reason `ERROR_CODES`'
   * own comment on `MEMBERSHIP_SUSPENDED` gives. Thrown by
   * `resolveOrgMembership` only when a membership row is found; a missing
   * row still returns null (NOT_A_MEMBER), collapsing "no such org" and
   * "never a member" exactly as before.
   */
  membershipSuspended: (message = 'Your membership in this organization has been suspended.') =>
    new AppError('MEMBERSHIP_SUSPENDED', message),

  orgSuspended: (message = 'This organization has been suspended.') =>
    new AppError('ORG_SUSPENDED', message),

  /**
   * Distinct from `orgSuspended` on purpose (Phase 12 Wave 3,
   * ai/phase-12-wave3.md §3.2) — an Owner staring at a locked-out org needs
   * to know WHICH wall they hit ("pay us" vs. "call support"). Thrown by
   * `resolveOrgMembership` only when `billing_status = 'canceled'`, never
   * for `trialing`/`active`/`past_due`.
   */
  orgBillingLapsed: (
    message = 'This organization’s trial or subscription has ended. An owner can resolve this from Billing settings.',
  ) => new AppError('ORG_BILLING_LAPSED', message),

  /**
   * The caller's ROLE permits this and their PLAN does not (Phase 12 Wave 4,
   * ai/phase-12-wave4-plans.md §3.4).
   *
   * Never a substitute for `forbidden()`. `can()` has already passed by the
   * time this is thrown — that ordering is what makes a plan check able only
   * to remove access, and it is why this code can carry the module name
   * without leaking anything: the caller was entitled to know the module
   * exists, they simply are not on a plan that includes it.
   *
   * The message names what to do, because unlike every other 403 in this list
   * there IS something the reader can do about it.
   */
  planRequired: (
    feature: string,
    message = `Your plan does not include ${feature}. An owner can upgrade from Billing settings.`,
    /* `details`, so the client can offer an upgrade CTA naming the specific
       module rather than parsing it back out of a sentence. */
  ) => new AppError('PLAN_REQUIRED', message, { details: { feature } }),

  /**
   * Use for resources the caller may not see, as well as those that do not
   * exist. Returning FORBIDDEN for an inaccessible resource confirms it exists,
   * which is an information leak across tenants (§8.7).
   */
  notFound: (message = 'Not found.') => new AppError('NOT_FOUND', message),

  conflict: (message = 'The resource was modified by someone else. Reload and try again.') =>
    new AppError('CONFLICT', message),

  validation: (details: Record<string, unknown>, message = 'The request was invalid.') =>
    new AppError('VALIDATION_FAILED', message, { details }),

  rateLimited: (retryAfterSeconds: number, message = 'Too many requests. Please slow down.') =>
    new AppError('RATE_LIMITED', message, { retryAfterSeconds }),

  quotaExceeded: (message = 'Quota exceeded for this organization.') =>
    new AppError('QUOTA_EXCEEDED', message),

  serviceUnavailable: (message = 'Temporarily unavailable. Please try again.') =>
    new AppError('SERVICE_UNAVAILABLE', message),

  internal: (cause?: unknown, message = 'Something went wrong.') =>
    new AppError('INTERNAL_ERROR', message, { cause }),
} as const;
